import { useCreateSquareMutation } from '@/api/squares/createSquare';
import { useUpdateSquareMutation } from '@/api/squares/updateSquare';
import {
  SquareFormValues,
  SquareData,
  apiToFormValues,
  formValuesToCreatePayload,
  formValuesToUpdatePayload,
} from '@/utils/forms/squareTransformers';

interface UseSquareFormProps {
  square?: SquareData | null;
  searchLeagueId?: string;
  isNewSquare: boolean;
}

export function useSquareForm({ square, searchLeagueId, isNewSquare }: UseSquareFormProps) {
  const { mutate: createMutate, isPending: createIsPending } = useCreateSquareMutation();
  const { mutate: updateMutate, isPending: updateIsPending } = useUpdateSquareMutation();

  const initialValues = apiToFormValues(square, searchLeagueId);

  const handleSubmit = async (values: SquareFormValues, { setSubmitting }: any) => {
    try {
      // Determine if this is an update - check for either squares_contest_id or question_id
      const contestId = square?.squares_contest_id || square?.question_id;

      if (!isNewSquare && contestId) {
        const updatePayload = formValuesToUpdatePayload(contestId, values);

        await new Promise((resolve, reject) => {
          updateMutate(updatePayload, {
            onSuccess: response => {
              resolve(response);
            },
            onError: error => {
              console.error('Update error:', error);
              reject(error);
            },
          });
        });
      } else {
        const createPayload = formValuesToCreatePayload(values);

        await new Promise((resolve, reject) => {
          createMutate(createPayload, {
            onSuccess: response => {
              resolve(response);
            },
            onError: error => {
              console.error('Create error:', error);
              reject(error);
            },
          });
        });
      }
    } catch (error) {
      console.error('Failed to save squares contest:', error);
      throw error; // Re-throw so Formik can handle it
    } finally {
      setSubmitting(false);
    }
  };

  return {
    initialValues,
    handleSubmit,
    isPending: createIsPending || updateIsPending,
  };
}
